/**
 * Player settings (spec §8). Persisted to localStorage and mirrored to D1.
 * `apply()` reflects settings into the DOM (classes / CSS vars / starfield).
 */

export type TierNotation = "name" | "roman" | "number" | "none";
export type ParticleLevel = "high" | "normal" | "low" | "off";
export type AnimSpeed = "slow" | "normal" | "fast" | "instant";
export type DropControl = "tap" | "drag";

export interface Settings {
  // 8.1 display
  tierNotation: TierNotation;
  numberAbbrev: boolean;
  // 8.2 sound
  sfx: boolean;
  sfxVolume: number;
  bgm: boolean;
  bgmVolume: number;
  haptics: boolean;
  // 8.3 graphics & performance
  particles: ParticleLevel;
  glow: boolean;
  animSpeed: AnimSpeed;
  screenShake: boolean;
  starfield: boolean;
  reduceMotion: boolean;
  // 8.4 accessibility
  highContrast: boolean;
  fontScale: number;
  // 8.5 gameplay
  dropControl: DropControl;
  previewCount: 1 | 2;
  /**
   * Board width applied to ALL modes (campaign/daily/endless). Chosen once on
   * the home screen; wider boards unlock by clearing World 1 at the prior width
   * (4 → clear W1 → 5 → clear W1 at 5 → 6).
   */
  boardCols: number;
  // identity (leaderboard display name; empty => use generated handle)
  displayName: string;
  /** Tier (1..12) chosen as the profile avatar; 0 => default/none. */
  profileTier: number;
  // cosmetics (must be owned in progress.ownedCosmetics, except the defaults)
  /** Equipped tile skin: "classic" (default) or a purchased skin id. */
  tileSkin: string;
  /** Equipped board theme: "default" or a purchased theme id. */
  boardTheme: string;
  /** Equipped achievement-title id shown on the leaderboard ("" = none). */
  activeTitle: string;
  // sync
  updatedAt: number;
}

const KEY = "cm.settings.v1";

export function defaultSettings(): Settings {
  return {
    // Tiles show the celestial art only by default; the evolution ladder at the
    // top of the play screen is the reference. Players can re-enable labels.
    tierNotation: "none",
    numberAbbrev: true,
    sfx: true,
    sfxVolume: 0.7,
    bgm: false,
    bgmVolume: 0.4,
    haptics: true,
    particles: "normal",
    glow: true,
    animSpeed: "normal",
    screenShake: true,
    starfield: true,
    reduceMotion: false,
    highContrast: false,
    fontScale: 1,
    dropControl: "tap",
    previewCount: 2,
    boardCols: 4,
    displayName: "",
    profileTier: 0,
    tileSkin: "classic",
    boardTheme: "default",
    activeTitle: "",
    updatedAt: 0,
  };
}

/** Multiplier applied to animation timings (1 = normal, lower = faster). */
export const ANIM_SPEED_MULT: Record<AnimSpeed, number> = {
  slow: 1.6,
  normal: 1,
  fast: 0.55,
  instant: 0.001,
};

type Listener = (s: Readonly<Settings>) => void;

class SettingsStore {
  private data: Settings;
  private listeners = new Set<Listener>();

  constructor() {
    this.data = this.load();
  }

  private load(): Settings {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return { ...defaultSettings(), ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return defaultSettings();
  }

  private persist(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      /* ignore */
    }
    for (const fn of this.listeners) fn(this.data);
  }

  get snapshot(): Readonly<Settings> {
    return this.data;
  }

  get<K extends keyof Settings>(key: K): Settings[K] {
    return this.data[key];
  }

  set<K extends keyof Settings>(key: K, value: Settings[K]): void {
    if (this.data[key] === value) return;
    this.data[key] = value;
    this.data.updatedAt = Date.now();
    this.persist();
    this.apply();
  }

  /** Replace all settings (e.g. after a server pull). Keeps incoming updatedAt. */
  replace(data: Partial<Settings>): void {
    this.data = { ...defaultSettings(), ...data };
    this.persist();
    this.apply();
  }

  resetToDefaults(): void {
    this.data = { ...defaultSettings(), updatedAt: Date.now() };
    this.persist();
    this.apply();
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Reflect settings into the document. Safe to call when there is no DOM. */
  apply(): void {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const s = this.data;
    root.classList.toggle("no-glow", !s.glow);
    root.classList.toggle("high-contrast", s.highContrast);
    root.classList.toggle("reduce-motion", s.reduceMotion);
    root.classList.toggle("no-starfield", !s.starfield);
    root.dataset.particles = s.particles;
    root.dataset.animspeed = s.animSpeed;
    // Cosmetics: tile skin (CSS filter on tile art) + board theme (palette).
    root.dataset.skin = s.tileSkin || "classic";
    root.dataset.boardTheme = s.boardTheme || "default";
    root.style.setProperty("--font-scale", String(s.fontScale));
    root.style.setProperty("--anim-mult", String(ANIM_SPEED_MULT[s.animSpeed]));
  }
}

export const settings = new SettingsStore();
